import {
  IOnboardingReview,
  IOnboardingServices,
  IOnboardingStep2Context,
} from "../../../Interfaces/IOnboardingWizardState";
import {
  activeServices,
  duplicateServiceName,
  includesHealthTick,
  includesTdda,
  isAlcoholInPlay,
  isAlcoholOnly,
  isLabChosenByClient,
  labsForMethod,
  METHOD_ORAL_FLUID,
  METHOD_URINE,
  Step2ServiceReferences,
  TEST_TYPE_PRE_DEFINED,
  validateService,
} from "./Step2.rules";

/**
 * Step 2's validation, in the order story 5629 states it — the same conditions the server's
 * completion engine applies, so a complete progress bar always means Save and Continue will pass
 * (decision 12, superseding S2.23's looser rule).
 *
 * A pending HealthTick review is checked last, after every data-entry issue an RM actually can fix,
 * so a form that is still genuinely incomplete keeps reporting the more specific reason (bug 5859).
 */
export const validateStep2 = (
  services: IOnboardingServices | null,
  context: IOnboardingStep2Context,
  labels: {
    method: (key: string) => string;
    occasion: (key: string) => string;
  },
  healthTickReview?: IOnboardingReview | null,
  references: Step2ServiceReferences & {
    step2HairTestReasons?: { key: string; label: string }[];
    step2HairPanels?: { key: string; label: string }[];
    step2NailPanels?: { key: string; label: string }[];
    step2SpecialistPanels?: { key: string; label: string }[];
  } = {},
): string | null => {
  if (!services) return "Choose a service line for this client.";

  if (includesTdda(services.serviceLine)) {
    const active = activeServices(services);
    const anyMethod =
      services.urineEnabled ||
      services.oralFluidEnabled ||
      services.hairEnabled ||
      services.nailEnabled;
    const anyAlcoholOnly = active.some(isAlcoholOnly);
    const anyOtherService =
      Boolean(services.otherServices?.policyDevelopment) ||
      Boolean(services.otherServices?.training) ||
      Boolean(services.otherServices?.other);

    if (!anyMethod && !anyAlcoholOnly && !anyOtherService)
      return "Select at least one collection method.";

    if (services.urineEnabled && !active.some((x) => x.method === METHOD_URINE))
      return "Add at least one urine testing service.";
    if (services.oralFluidEnabled && !active.some((x) => x.method === METHOD_ORAL_FLUID))
      return "Add at least one oral fluid testing service.";

    for (const service of active) {
      const failure = validateService(service, references);
      if (failure) return failure;
    }

    const duplicate = duplicateServiceName(services);
    if (duplicate)
      return `Give each testing service a different name — "${duplicate}" is used more than once.`;

    // A ticked screened method with a service needs a valid confirmation lab (S2.11) — but only
    // where the client is the one who answers. An Australian client never chooses (task 6008).
    if (isLabChosenByClient(context)) {
      const screened: [string, boolean, string | null][] = [
        [METHOD_URINE, services.urineEnabled, services.urineLab],
        [METHOD_ORAL_FLUID, services.oralFluidEnabled, services.oralFluidLab],
      ];
      for (const [method, enabled, lab] of screened) {
        if (!enabled || !active.some((x) => x.method === method)) continue;
        if (!lab || !labsForMethod(method, context).includes(lab))
          return `Choose a confirmation laboratory for ${labels.method(method)}.`;
      }
    }

    // A ticked specialist method with an included card needs its card completed (S2.7), on a
    // reason hair offers (story 6359), with a panel offered for that method.
    const hairReasons = (references.step2HairTestReasons || []).map((x) => x.key);
    const specialist: [string, boolean, typeof services.hairOccasions, { key: string; label: string }[]][] = [
      ["Hair", services.hairEnabled, services.hairOccasions || [], references.step2HairPanels || []],
      ["Nail", services.nailEnabled, services.nailOccasions || [], references.step2NailPanels || []],
    ];
    for (const [method, enabled, cards, panels] of specialist) {
      if (!enabled) continue;
      for (const card of cards.filter((x) => x.included)) {
        const where = `${labels.method(method)} ${labels.occasion(card.occasionCode)}`;
        if (hairReasons.length && !hairReasons.includes(card.occasionCode))
          return `Choose a test reason offered for ${labels.method(method)} — ${labels.occasion(card.occasionCode)} is not a hair or nail reason.`;
        if (!card.testType) return `Choose a test type for ${where}.`;
        if (card.testType !== TEST_TYPE_PRE_DEFINED) continue;
        if (!card.testReason?.trim()) return `Enter a test reason for ${where}.`;
        if (!card.serviceLines.length) return `Add a service line for ${where}.`;
        const incomplete = card.serviceLines.some(
          (line) =>
            !line.serviceName?.trim() ||
            line.periodMonths == null ||
            !line.panelKey ||
            line.alternativeSamplePermitted == null,
        );
        if (incomplete) return `Complete every service line for ${where}.`;
        if (panels.length) {
          const stale = card.serviceLines.find((line) => !panels.some((p) => p.key === line.panelKey));
          if (stale) {
            const label =
              (references.step2SpecialistPanels || []).find((p) => p.key === stale.panelKey)?.label ??
              stale.panelKey;
            return `Choose a ${method.toLowerCase()} panel for ${where} — "${label}" is not offered.`;
          }
        }
      }
    }

    if (isAlcoholInPlay(services)) {
      if (!services.alcoholBandUnder20?.trim())
        return "Enter the alcohol cut-off for under 20 year olds.";
      if (!services.alcoholBand20Plus?.trim())
        return "Enter the alcohol cut-off for 20 years and over.";
    }
  }

  if (includesHealthTick(services.serviceLine)) {
    const healthTick = services.healthTick;
    const anySelected =
      Boolean(healthTick?.pemSelected) ||
      Boolean(healthTick?.ahmSelected) ||
      Boolean(healthTick?.fluSelected) ||
      Boolean(healthTick?.dl9Selected);
    if (!anySelected) return "Select at least one HealthTick service.";

    if (healthTick.pemSelected && !(healthTick.pemLevels || []).length)
      return "Choose a level for Pre-Employment Medical (PEM).";
    if (healthTick.ahmSelected && !(healthTick.ahmLevels || []).length)
      return "Choose a level for Annual Health Monitoring (AHM).";

    const preEmployment = healthTick.htPreEmployment;
    if (healthTick.pemSelected && preEmployment?.included) {
      const method = preEmployment.method || METHOD_URINE;
      if (
        !preEmployment.confirmationLab ||
        !labsForMethod(method, context).includes(preEmployment.confirmationLab)
      )
        return "Choose a confirmation laboratory for the HT pre-employment test.";
    }
  }

  // Checked last: everything an RM can actually fix has already passed by this point, so a pending
  // review is genuinely the only thing left blocking the step (bug 5859).
  if (healthTickReview?.status === 1) return "A HealthTick review is pending.";

  return null;
};
